import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from '../lib/routing';
import { BookOpen, Hourglass, Microphone, SpeakerHigh, UploadSimple } from '@phosphor-icons/react';
import Header from '../components/header';
import TypingDots from '../components/typing-dots';
import { persistVoiceMessages, touchThreadActivity } from '../lib/voice-persist';
import { WebVoiceSession, type VoiceState } from '../lib/voice-session';
import { formatWidgetResult } from '@naia-app/widgets';
import {
  getThread,
  listMessages,
  readableResult,
  sendMessageStream,
  uploadAttachment,
  CHAT_MODE_ICON,
  CHAT_MODE_LABEL,
  type ChatAttachment,
  type ChatMessage,
  type ChatMode,
  type ChatThread,
  type SendMessageInput,
  type WidgetEvent,
} from '../lib/chat-api';
import CourseSectionsPanel from '../components/course-sections-panel';
import { ToolBlockList } from '../components/tool-block';
import DocumentDrawer from '../components/document-drawer';
import type { SectionUpdatedEvent, SectionCreatedEvent, SectionsReorderedEvent } from '../lib/chat-api';
import type { ToolCall } from '../lib/editor-chat';
import { CHAT_SLOW_NOTICE, chatAttachmentErrorMessage, chatErrorMessage } from '../lib/chat-errors';
import { newClientSendId, planRetry } from '../lib/turn-recovery';
import { RetryAlert, SlowNotice } from '../components/chat-turn-notices';

/* Generous cap on how long the model stays paused waiting for the kid to answer */
/* an interactive voice widget before it gives up and continues. */
const VOICE_WIDGET_TIMEOUT_MS = 180_000;
const WIDGET_MAX_BYTES = 50_000;

import { toUiMessage, WIDGET_TOOL_NAMES, type UiMessage } from './chat/message-mapping';
import { isImageMime } from './chat/attachments';
import { Bubble } from './chat/bubble';
import { ChatComposer } from './chat/composer';
import { useRoles } from '../hooks/use-roles';
import { WidgetView } from './chat/widget-view';
import ChatMcqModal, { type McqAnswer } from '../components/chat-mcq-modal';
import ChatFormModal from '../components/chat-form-modal';
import { useChatAttachments } from './chat/use-chat-attachments';
import { useSendLock } from './chat/use-send-lock';
import { useSplitPanel } from './chat/use-split-panel';
import { APP_HEADER_HEIGHT_VAR } from '../components/shell/app-header';
import { Alert } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

const coursBtnBase = "flex-none inline-flex items-center gap-[6px] px-[14px] py-2 min-h-[40px] rounded-sm border-[1.5px] border-border bg-card font-extrabold text-[13px] cursor-pointer text-foreground";

const HISTORY_WINDOW = 200;

export default function Chat() {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const [thread, setThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const { activeRole } = useRoles();
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState('');
  const [streamWidgets, setStreamWidgets] = useState<WidgetEvent[]>([]);
  /* Ref mirror so the onDone closure can read the widgets produced this turn. */
  const streamWidgetsRef = useRef<WidgetEvent[]>([]);
  /* toolCallIds of interactive widgets the user has already answered this session. */
  const [answered, setAnswered] = useState<Set<string>>(new Set());
  /* toolCallIds dismissed by the user (hidden without submitting, resets on reload). */
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [slow, setSlow] = useState(false);
  const [partialText, setPartialText] = useState('');
  const failedTurnRef = useRef<{ input: SendMessageInput; knownBefore: number; matchText: string } | null>(null);
  const { pending, dropHover, imageInputRef, docInputRef, pickFiles, removePending, clearPending, dragProps } =
    useChatAttachments({ disabled: streaming, onError: setError });
  const guardSend = useSendLock();
  const abortRef = useRef<AbortController | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  /* ---- curriculum tool chips + BO document drawer (cours mode) ---------- */
  const [streamTools, setStreamTools] = useState<ToolCall[]>([]);
  const streamToolsRef = useRef<ToolCall[]>([]);
  const [drawer, setDrawer] = useState<{ docId: string; page: number } | null>(null);
  const openDoc = (docId: string, page: number) => setDrawer({ docId, page });

  const { isWide, panelWidth, splitRef, onDividerPointerDown, onDividerPointerMove, onDividerPointerUp } =
    useSplitPanel();

  /* ---- sections panel (cours mode, new structured editor) --------------- */
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [incomingSection, setIncomingSection] = useState<SectionUpdatedEvent | null>(null);
  const [incomingCreated, setIncomingCreated] = useState<SectionCreatedEvent | null>(null);
  const [incomingReordered, setIncomingReordered] = useState<SectionsReorderedEvent | null>(null);

  /* ---- voice (merged into this same thread) ---------------------------- */
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [voicePartialUser, setVoicePartialUser] = useState('');
  const [voicePartialAssistant, setVoicePartialAssistant] = useState('');
  const voiceSessionRef = useRef<WebVoiceSession | null>(null);
  /* Resolver for the interactive voice widget the model is paused on, if any. */
  const voicePendingRef = useRef<{ resolve: (r: unknown) => void; timeout: ReturnType<typeof setTimeout> } | null>(null);
  /* Always-fresh snapshot of messages for building the voice history seed */
  /* without making startVoice depend on (and re-create with) every keystroke. */
  const messagesRef = useRef<UiMessage[]>([]);
  messagesRef.current = messages;
  const voiceLive = voiceState !== 'idle' && voiceState !== 'error';

  /* First unanswered interactive widget of a given name. */
  /* Scans backwards through history and stops at the first user message — any */
  /* widget before a user turn was already answered (survives page reloads). */
  const findActiveWidget = (name: string): WidgetEvent | null => {
    const fromStream = streamWidgets.filter(
      (w) => w.name === name && w.toolCallId && !answered.has(w.toolCallId),
    );
    if (fromStream.length) return fromStream[fromStream.length - 1]!;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i]!;
      if (msg.role === 'user') return null;
      const ws = msg.widgets ?? [];
      /* If any widget in this assistant turn was already answered, the turn is done. */
      /* This also prevents a second ask_form from popping up if the LLM mistakenly */
      /* generated two in one response. */
      if (ws.some((w) => w.toolCallId && answered.has(w.toolCallId))) return null;
      for (let j = ws.length - 1; j >= 0; j--) {
        const w = ws[j]!;
        if (w.name === name && w.toolCallId && !dismissed.has(w.toolCallId)) return w;
      }
    }
    return null;
  };

  const activeMcq = useMemo<WidgetEvent | null>(
    () => findActiveWidget('ask_mcq'),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [streamWidgets, messages, answered, dismissed],
  );

  const activeForm = useMemo<WidgetEvent | null>(
    () => findActiveWidget('ask_form'),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [streamWidgets, messages, answered, dismissed],
  );

  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    (async () => {
      try {
        const [t, m] = await Promise.all([getThread(threadId), listMessages(threadId)]);
        if (cancelled) return;
        if (!t) {
          navigate('/app');
          return;
        }
        setThread(t);
        setMessages(m.map(toUiMessage));
        setAnswered(new Set());
        if (t.mode === 'cours' && !cancelled && t.document_id) {
          setIncomingSection(null);
          setIncomingCreated(null);
          setIncomingReordered(null);
          setSectionsOpen(window.matchMedia('(min-width: 900px)').matches);
        } else {
          setSectionsOpen(false);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setError(chatErrorMessage(e));
      }
    })();
    return () => {
      cancelled = true;
      /* On n'abort PAS le stream en cours : changer de conversation laisse la */
      /* génération se finir en arrière-plan (le serveur persiste le message), */
      /* au lieu de la perdre. L'historique se recharge au retour. */
    };
  }, [threadId, navigate]);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, streamBuffer, streamWidgets]);

  /* Stream one assistant turn (used by both a fresh user message and an */
  /* interactive-widget resume). Appends the assistant bubble on done. */
  const runTurn = async (input: SendMessageInput, knownBefore: number, matchText: string) => {
    if (!threadId) return;
    failedTurnRef.current = { input, knownBefore, matchText };
    setError(null);
    setStreaming(true);
    setSlow(false);
    setPartialText('');
    setStreamBuffer('');
    setStreamWidgets([]);
    streamWidgetsRef.current = [];
    setStreamTools([]);
    streamToolsRef.current = [];

    const ac = new AbortController();
    abortRef.current = ac;
    let acc = '';
    try {
      await sendMessageStream(
        input,
        {
          onDelta: (chunk) => { acc += chunk; setStreamBuffer((b) => b + chunk); },
          onSlow: () => setSlow(true),
          onActive: () => setSlow(false),
          onWidget: (w) => {
            streamWidgetsRef.current = [...streamWidgetsRef.current, w];
            setStreamWidgets(streamWidgetsRef.current);
          },
          onSectionUpdated: (s) => { setIncomingSection(s); setSectionsOpen(true); },
          onSectionCreated: (s) => { setIncomingCreated(s); setSectionsOpen(true); },
          onSectionsReordered: (e) => { setIncomingReordered(e); },
          onTool: (name, args) => {
            /* Widgets (render_widget/…) arrive via onWidget and render as iframes; */
            /* don't also list them as data-tool blocks (keeps live == reload). */
            if (WIDGET_TOOL_NAMES.has(name)) return;
            streamToolsRef.current = [...streamToolsRef.current, { name, args: args as ToolCall['args'] }];
            setStreamTools(streamToolsRef.current);
          },
          onToolResult: (name, refs, count, levels) => {
            /* Attach the result to the latest pending entry of this tool. */
            const arr = [...streamToolsRef.current];
            for (let i = arr.length - 1; i >= 0; i--) {
              if (arr[i]!.name === name && !arr[i]!.refs) {
                arr[i] = { ...arr[i]!, refs, itemCount: count, levels };
                break;
              }
            }
            streamToolsRef.current = arr;
            setStreamTools(arr);
          },
          onRetract: () => { acc = ''; setStreamBuffer(''); },
          onDone: (full) => {
            failedTurnRef.current = null;
            const ws = streamWidgetsRef.current;
            const ts = streamToolsRef.current;
            setMessages((prev) => [
              ...prev,
              {
                id: `tmp-resp-${Date.now()}`,
                thread_id: threadId,
                role: 'assistant',
                transcript: full,
                attachments: null,
                created_at: new Date().toISOString(),
                widgets: ws.length ? ws : undefined,
                tools: ts.length ? ts : undefined,
              },
            ]);
            setStreamBuffer('');
            setStreamWidgets([]);
            streamWidgetsRef.current = [];
            setStreamTools([]);
            streamToolsRef.current = [];
          },
          onError: (err) => { setError(err.message); setPartialText(acc); },
        },
        ac.signal,
      );
    } catch (e2) {
      /* Lève avant/hors du flux SSE (auth, validation, panne non gérée côté
         serveur) : contrairement à `onError` ci-dessus (déjà sanitisé par le
         serveur), ce message n'a pas été écrit pour l'utilisateur. */
      setError(chatErrorMessage(e2));
      setPartialText(acc);
    } finally {
      setStreaming(false);
      setSlow(false);
      abortRef.current = null;
    }
  };

  const retryTurn = () => {
    const failed = failedTurnRef.current;
    if (!failed || !threadId || streaming) return;
    const tid = threadId;
    guardSend(async () => {
      setError(null);
      setStreaming(true);
      const plan = await planRetry(
        async () => (await listMessages(tid)).map((m) => ({ role: m.role, transcript: m.transcript, failed: m.turn_failed, clientSendId: m.metadata?.clientSendId })),
        failed.knownBefore,
        failed.matchText,
        HISTORY_WINDOW,
        failed.input.clientSendId,
      );
      if (plan === 'already-processed') {
        setMessages((await listMessages(tid)).map(toUiMessage));
        setPartialText('');
        failedTurnRef.current = null;
        setStreaming(false);
        return;
      }
      await runTurn(failed.input, failed.knownBefore, failed.matchText);
    });
  };

  /* Auto-init for 'cours' mode: fire Naïa's opening question when the thread */
  /* is freshly created (no messages). Uses a ref so runTurn is always current. */
  const runTurnRef = useRef(runTurn);
  runTurnRef.current = runTurn;
  const coursAutoInitFiredRef = useRef(false);
  useEffect(() => {
    if (!thread || thread.mode !== 'cours' || messages.length > 0 || streaming) return;
    if (coursAutoInitFiredRef.current) return;
    coursAutoInitFiredRef.current = true;
    runTurnRef.current({
      threadId: thread.id,
      text: 'Bonjour',
      mode: 'cours',
      parcoursId: thread.parcours_id ?? null,
      attachments: [],
      history: [],
    }, 0, 'Bonjour');
  }, [thread, messages.length, streaming]);

  /* Point d'entrée UNIQUE de l'envoi — onSubmit du formulaire comme raccourci
     clavier. Un second chemin qui appellerait runTurn directement contournerait
     le verrou et rouvrirait le doublon. */
  const submit = (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    if (!threadId || !thread || streaming) return;
    const text = draft.trim();
    if (!text && pending.length === 0) return;
    const tid = threadId;
    const th = thread;

    /* `streaming` monte AVANT le téléversement, pas à l'entrée de runTurn :
       sinon le bouton reste actif pendant tout l'envoi du fichier, et un second
       clic génère — et facture — une seconde réponse. Le verrou de useSendLock
       couvre en plus les clics d'un même tick, que ce drapeau d'état manque. */
    guardSend(async () => {
      setError(null);
      setStreaming(true);
      try {
        /* Upload pending files to Storage (sequentially so errors are visible). */
        const uploaded: ChatAttachment[] = [];
        for (const p of pending) {
          try {
            uploaded.push(await uploadAttachment({ threadId: tid, file: p.file }));
          } catch (err) {
            setError(chatAttachmentErrorMessage(err));
            return;
          }
        }

        /* Attachment-only sends get a short natural text in the USER'S voice — it */
        /* shows in their bubble and must not hijack the flow (the old hardcoded */
        /* « Décris ce que tu vois sur cette image » made Naïa describe a teacher's */
        /* PDF instead of advancing the cours workflow). */
        const onlyImages = uploaded.length > 0 && uploaded.every((a) => isImageMime(a.mime));
        const attachFallback = th.mode === 'cours'
          ? 'Voici mon document.'
          : onlyImages
            ? 'Regarde cette image.'
            : 'Regarde ce document.';

        /* Optimistic user bubble + clear composer state. */
        const optimistic: ChatMessage = {
          id: `tmp-${Date.now()}`,
          thread_id: tid,
          role: 'user',
          transcript: text || attachFallback,
          attachments: uploaded.length > 0 ? uploaded : null,
          created_at: new Date().toISOString(),
        };
        const nextHistory = [...messages, optimistic];
        setMessages(nextHistory);
        setDraft('');
        clearPending();

        await runTurn({
          threadId: tid,
          text: text || attachFallback,
          mode: th.mode === 'voice' ? 'general' : th.mode,
          parcoursId: th.parcours_id,
          attachments: uploaded,
          clientSendId: newClientSendId(),
          history: nextHistory.map((m) => ({ role: m.role, transcript: m.transcript })),
        }, messages.length, optimistic.transcript);
      } finally {
        setStreaming(false);
      }
    });
  };

  /* User answered an interactive widget (ask_mcq OR render_interactive) → */
  /* resume the paused turn with their result. */
  const resumeWidget = async (widget: WidgetEvent, result: unknown) => {
    if (!threadId || !thread || streaming || !widget.toolCallId) return;
    if (answered.has(widget.toolCallId)) return;
    let label = '↳ (réponse)';
    if (widget.name === 'ask_mcq') {
      const r = result as { selectedIndices?: number[]; freeText?: string };
      if (r.freeText) {
        label = `↳ ${r.freeText}`;
      } else {
        const opts = ((widget.args as { options?: string[] })?.options) ?? [];
        const idx = r.selectedIndices ?? [];
        const labels = idx.map((i) => opts[i]).filter(Boolean) as string[];
        if (labels.length) label = `↳ ${labels.join(', ')}`;
      }
    } else {
      const r = formatWidgetResult(widget.name ?? '', widget.args, result);
      if (r) label = `↳ ${r}`;
    }
    setAnswered((prev) => new Set(prev).add(widget.toolCallId!));

    const historySnapshot = messages.map((m) => ({ role: m.role, transcript: m.transcript }));
    setMessages((prev) => [
      ...prev,
      {
        id: `tmp-ans-${Date.now()}`,
        thread_id: threadId,
        role: 'user',
        transcript: label,
        attachments: null,
        created_at: new Date().toISOString(),
      },
    ]);

    await runTurn({
      threadId,
      text: '',
      mode: thread.mode === 'voice' ? 'general' : thread.mode,
      parcoursId: thread.parcours_id,
      history: historySnapshot,
      clientSendId: newClientSendId(),
      resume: { name: widget.name ?? 'ask_mcq', args: widget.args, result },
    }, historySnapshot.length, label);
  };

  /* ---- voice wired into THIS thread (no separate voice mode) ----------- */
  /* Append message(s) to the live list AND persist them to the same `messages` */
  /* table the text chat uses, so voice turns + widgets replay on reload. */
  const appendVoiceMessages = (ui: UiMessage[], rows: Record<string, unknown>[]) => {
    if (!threadId || ui.length === 0) return;
    setMessages((prev) => [...prev, ...ui]);
    void persistVoiceMessages(rows);
    void touchThreadActivity(threadId);
  };

  /* La transcription est écrite par le proxy WS, qui la voyait déjà passer. Ici
     on ne fait plus que l'afficher : la ré-écrire la dupliquerait. */
  const showVoiceMessages = (ui: UiMessage[]) => {
    if (ui.length === 0) return;
    setMessages((prev) => [...prev, ...ui]);
  };

  /* The kid answered an interactive voice widget → ship the value back over the */
  /* open WS (resolves the paused tool call) and record their answer bubble. */
  const submitVoiceWidget = (widget: WidgetEvent, value: unknown) => {
    const p = voicePendingRef.current;
    if (!p || !widget.toolCallId || !threadId || answered.has(widget.toolCallId)) return;
    clearTimeout(p.timeout);
    voicePendingRef.current = null;
    setAnswered((prev) => new Set(prev).add(widget.toolCallId!));
    const label = readableResult(value);
    const text = label ? `↳ ${label}` : '↳ (réponse)';
    const now = new Date().toISOString();
    appendVoiceMessages(
      [{ id: `vw-u-${Date.now()}`, thread_id: threadId, role: 'user', transcript: text, attachments: null, created_at: now }],
      [{ thread_id: threadId, role: 'user', transcript: text, created_at: now }],
    );
    p.resolve({ value });
  };

  const startVoice = async () => {
    const tid = threadId;
    if (!tid || !thread || voiceLive) return;
    setError(null);
    setVoicePartialUser('');
    setVoicePartialAssistant('');

    /* Seed the Live session with the recent text turns so Naïa keeps context */
    /* across the text→voice switch (token carries historyConfig). */
    const initialHistory = messagesRef.current
      .filter((m) => m.transcript && m.transcript.trim())
      .slice(-20)
      .map((m) => ({ role: m.role === 'assistant' ? ('model' as const) : ('user' as const), text: m.transcript }));

    const session = new WebVoiceSession({
      onState: setVoiceState,
      onError: (e) => setError(e.message),
      onTranscript: (txt, role) => {
        if (role === 'user') setVoicePartialUser(txt);
        else setVoicePartialAssistant(txt);
      },
      onTurnComplete: ({ user, assistant }) => {
        const now = new Date().toISOString();
        const ui: UiMessage[] = [];
        if (user.trim()) {
          ui.push({ id: `v-u-${Date.now()}`, thread_id: tid, role: 'user', transcript: user, attachments: null, created_at: now });
        }
        if (assistant.trim()) {
          ui.push({ id: `v-a-${Date.now() + 1}`, thread_id: tid, role: 'assistant', transcript: assistant, attachments: null, created_at: now });
        }
        showVoiceMessages(ui);
        setVoicePartialUser('');
        setVoicePartialAssistant('');
      },
      onToolCall: (name, args) => {
        const a = (args ?? {}) as { html?: string; title?: string };
        const html = String(a.html ?? '');
        if (html.length > WIDGET_MAX_BYTES) return { error: `widget html exceeded ${WIDGET_MAX_BYTES} bytes` };
        const interactive = name === 'render_interactive';
        if (name !== 'render_widget' && !interactive) return { error: `unknown tool: ${name}` };
        const toolCallId = `vw-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const widget: WidgetEvent = { name: name as WidgetEvent['name'], toolCallId, interactive, args, html: a.html, title: a.title, voice: true };
        const now = new Date().toISOString();
        appendVoiceMessages(
          [{ id: `vw-a-${toolCallId}`, thread_id: tid, role: 'assistant', transcript: '', attachments: null, created_at: now, widgets: [widget] }],
          [{ thread_id: tid, role: 'assistant', transcript: '', attachments: [{ kind: 'widget', toolCallId, name, args, voice: true }], created_at: now }],
        );
        if (!interactive) return { ok: true, rendered: true };
        /* Supersede any stale pending widget before showing the new one. */
        if (voicePendingRef.current) {
          clearTimeout(voicePendingRef.current.timeout);
          voicePendingRef.current.resolve({ value: null, superseded: true });
          voicePendingRef.current = null;
        }
        /* Keep the model paused (blocking) until the kid submits / timeout. */
        return new Promise<unknown>((resolve) => {
          const timeout = setTimeout(() => {
            voicePendingRef.current = null;
            resolve({ value: null, timedOut: true });
          }, VOICE_WIDGET_TIMEOUT_MS);
          voicePendingRef.current = { resolve, timeout };
        });
      },
    }, 'general', tid);
    voiceSessionRef.current = session;
    await session.start({ initialHistory });
  };

  /* La consommation est enregistrée par le proxy, à la fermeture de l'amont.
     La redéclarer ici referait la double facturation corrigée en #519. */
  const stopVoice = async () => {
    if (voicePendingRef.current) {
      clearTimeout(voicePendingRef.current.timeout);
      voicePendingRef.current.resolve({ value: null, cancelled: true });
      voicePendingRef.current = null;
    }
    await voiceSessionRef.current?.stop();
    voiceSessionRef.current = null;
    setVoicePartialUser('');
    setVoicePartialAssistant('');
  };

  const toggleVoice = () => {
    if (voiceLive) void stopVoice();
    else void startVoice();
  };

  /* Stop the voice session when leaving the chat. */
  useEffect(() => () => void voiceSessionRef.current?.stop(), []);

  /* render_interactive frames post their result up from the sandboxed iframe; */
  /* match it to the pending widget by toolCallId. Voice widgets answer over the */
  /* open Live WS; text widgets resume the turn via a new request. */
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { type?: string; toolCallId?: string; value?: unknown };
      if (!d || d.type !== 'naia:widget_result' && d.type !== 'pilo:widget_result' || typeof d.toolCallId !== 'string') return;
      const w =
        messages.flatMap((m) => m.widgets ?? []).find((x) => x.toolCallId === d.toolCallId) ??
        streamWidgets.find((x) => x.toolCallId === d.toolCallId);
      if (!w) return;
      if (w.voice) submitVoiceWidget(w, d.value);
      else void resumeWidget(w, d.value);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  });

  return (
    <div
      className="flex flex-col overflow-hidden relative"
      style={{ height: `calc(100dvh - var(${APP_HEADER_HEIGHT_VAR}, 0px))` }}
      {...dragProps}
    >
      {dropHover && (
        <div className="absolute inset-0 z-[980] bg-naia-sky border-[3px] border-dashed border-naia-blue rounded-[16px] grid place-items-center pointer-events-none">
          <div className="flex items-center gap-2 bg-card border-[1.5px] border-border rounded-[14px] p-[18px_28px] font-extrabold text-[16px] shadow-card text-foreground">
            <UploadSimple size={20} weight="bold" />
            Déposez vos images ou documents ici
          </div>
        </div>
      )}
      <Header floatingBurger />
      {thread && (
        <div className="w-full [padding:max(14px,env(safe-area-inset-top))_16px_8px_calc(72px+env(safe-area-inset-left))] border-b-[1.5px] border-border box-border flex-none min-h-[56px] flex flex-col justify-center">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <div className="font-extrabold text-[16px] text-foreground overflow-hidden text-ellipsis whitespace-nowrap min-w-0">{thread.title}</div>
                <span
                  aria-label={`Mode ${CHAT_MODE_LABEL[thread.mode as ChatMode] ?? thread.mode}`}
                  className="flex-none inline-flex items-center gap-1 px-[10px] py-1 min-h-[28px] rounded-full border-[1.5px] border-border bg-card font-bold text-[12px] text-muted-foreground leading-[1.2]"
                >
                  <span aria-hidden>{CHAT_MODE_ICON[thread.mode as ChatMode] ?? '💬'}</span>
                  {CHAT_MODE_LABEL[thread.mode as ChatMode] ?? thread.mode}
                </span>
              </div>
            </div>
            {thread.mode === 'cours' && thread.document_id && (
              <button
                type="button"
                onClick={() => setSectionsOpen((v) => !v)}
                className={cn(coursBtnBase, sectionsOpen && "bg-naia-green-bg")}
              >
                <BookOpen size={16} weight="bold" />
                Cours
              </button>
            )}
          </div>
        </div>
      )}

      <div ref={splitRef} className="flex flex-1 min-h-0">
      <div className="flex-1 min-w-0 flex flex-col">
      <div ref={scrollerRef} className="flex-1 min-h-0 overflow-auto p-4">
        <div className="max-w-[1060px] mx-auto flex flex-col gap-3">
          {messages.map((m, idx) => (
            <Fragment key={m.id}>
              <ToolBlockList tools={m.tools ?? []} onOpenDoc={openDoc} keyPrefix={`t-${m.id}-`} />
              <Bubble role={m.role} text={m.transcript} attachments={m.attachments} />
              {m.widgets?.map((w, wi) => {
                const isAnswered = !!(w.toolCallId && answered.has(w.toolCallId));
                const isDismissed = !!((w.name === 'ask_form' || w.name === 'ask_mcq') && w.toolCallId && dismissed.has(w.toolCallId) && !isAnswered && idx === messages.length - 1);
                return (
                  <div key={w.toolCallId ?? wi} className="self-start w-full">
                    <WidgetView
                      widget={w}
                      locked={
                        streaming ||
                        isAnswered ||
                        /* interactive widgets are only answerable on the last message */
                        (!!w.interactive && idx !== messages.length - 1)
                      }
                      onResult={(r) => resumeWidget(w, r)}
                    />
                    {isDismissed && (
                      <div className="flex items-center gap-2 py-1">
                        <span className="text-[12px] text-muted-foreground italic">
                          🕐 {(w.args as { title?: string } | undefined)?.title ?? (w.name === 'ask_mcq' ? 'Question en attente' : 'Formulaire en attente')}
                        </span>
                        <button
                          type="button"
                          onClick={() => setDismissed((prev) => { const s = new Set(prev); s.delete(w.toolCallId!); return s; })}
                          className="px-[10px] py-1 rounded-sm border-[1.5px] border-naia-muted-2 bg-naia-info-bg text-foreground text-[12px] font-bold cursor-pointer font-[inherit] min-h-[28px]"
                        >
                          Répondre ↩
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </Fragment>
          ))}
          {streaming && (
            <ToolBlockList
              tools={streamTools}
              liveFor={(t) => !t.refs && !t.itemCount}
              onOpenDoc={openDoc}
              keyPrefix="st-"
            />
          )}
          {streaming && streamBuffer && <Bubble role="assistant" text={streamBuffer} />}
          {streaming &&
            streamWidgets.map((w, wi) => (
              <div key={w.toolCallId ?? wi} className="self-start w-full">
                <WidgetView widget={w} locked onResult={() => {}} />
              </div>
            ))}
          {streaming && !streamBuffer && streamWidgets.length === 0 && <TypingDots />}
          {!streaming && partialText && <Bubble role="assistant" text={partialText} />}
          {streaming && slow && <SlowNotice text={CHAT_SLOW_NOTICE} />}
          {error && (failedTurnRef.current && !streaming
            ? <RetryAlert message={error} partial={!!partialText} onRetry={retryTurn} />
            : <Alert>{error}</Alert>)}
        </div>
      </div>

      {voiceLive && (
        <div className="max-w-[1060px] w-full mx-auto px-4 py-[6px] border-t border-border bg-naia-card-alt text-[12px] flex-none box-border">
          <div className="flex items-center gap-[6px] font-extrabold text-foreground">
            {voiceState === 'connecting' ? (
              <><Hourglass size={14} weight="bold" /> Naïa se prépare…</>
            ) : voiceState === 'speaking' ? (
              <><SpeakerHigh size={14} weight="bold" /> Naïa répond…</>
            ) : (
              <><Microphone size={14} weight="bold" /> Parle, je t'écoute</>
            )}
          </div>
          {voicePartialUser && <div className="text-muted-foreground mt-[2px]">{voicePartialUser}</div>}
          {voicePartialAssistant && <div className="text-foreground mt-[2px]">{voicePartialAssistant}</div>}
        </div>
      )}

      <ChatComposer
        placeholder={activeRole === 'eleve' ? 'Pose ta question…' : 'Posez votre question…'}
        draft={draft}
        onDraftChange={setDraft}
        onSubmit={submit}
        streaming={streaming}
        pending={pending}
        onRemovePending={removePending}
        imageInputRef={imageInputRef}
        docInputRef={docInputRef}
        onPickFiles={pickFiles}
        voiceLive={voiceLive}
        onToggleVoice={toggleVoice}
      />
      </div>

      {/* Sections panel (cours mode, new structured editor) — split on desktop, overlay on mobile. */}
      {thread?.mode === 'cours' && thread.document_id && sectionsOpen && isWide && !drawer && (
        <>
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Redimensionner le panneau sections"
            onPointerDown={onDividerPointerDown}
            onPointerMove={onDividerPointerMove}
            onPointerUp={onDividerPointerUp}
            className="w-2 flex-none cursor-col-resize bg-naia-card-alt border-l border-r border-border touch-none"
          />
          <div className="min-w-[320px] flex-none min-h-0" style={{ width: panelWidth }}>
            <CourseSectionsPanel
              key={thread.document_id}
              documentId={thread.document_id}
              incomingSection={incomingSection}
              incomingCreated={incomingCreated}
              incomingReordered={incomingReordered}
              onClose={() => setSectionsOpen(false)}
            />
          </div>
        </>
      )}


      </div>

      {/* BO document viewer (fixed overlay, right side) — masks panels while open. */}
      {drawer && <DocumentDrawer docId={drawer.docId} initialPage={drawer.page} onClose={() => setDrawer(null)} />}

      {thread?.mode === 'cours' && thread.document_id && sectionsOpen && !isWide && !drawer && (
        <div className="fixed inset-0 z-[950] bg-card pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] flex flex-col">
          <CourseSectionsPanel
            key={thread.document_id}
            documentId={thread.document_id}
            incomingSection={incomingSection}
            incomingCreated={incomingCreated}
            incomingReordered={incomingReordered}
            onClose={() => setSectionsOpen(false)}
          />
        </div>
      )}



      {activeMcq && (
        <ChatMcqModal
          widget={activeMcq}
          disabled={streaming}
          onAnswer={(ans: McqAnswer) =>
            void resumeWidget(
              activeMcq,
              ans.freeText
                ? { text: ans.freeText }
                : { ...ans, label: ans.selectedLabels.join(', ') },
            )
          }
          onDismiss={() => activeMcq.toolCallId && setDismissed((s) => new Set(s).add(activeMcq.toolCallId!))}
        />
      )}

      {activeForm && !activeMcq && (
        <ChatFormModal
          widget={activeForm}
          disabled={streaming}
          onSubmit={(formData) => void resumeWidget(activeForm, { formData })}
          onDismiss={() => activeForm.toolCallId && setDismissed((s) => new Set(s).add(activeForm.toolCallId!))}
        />
      )}
    </div>
  );
}
